import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';

// Decorative, finite bursts. Values are fixed so rerenders never regenerate
// particles, and no game timing, score or answer handler depends on an effect.
export function ComicBurst() {
  return <span className="qpug-comic-burst" aria-hidden="true">{Array.from({ length: 12 }, (_, index) => {
    const angle = index * Math.PI / 6;
    return <i key={index} style={{
      '--burst-x': `${Math.round(Math.cos(angle) * (46 + index % 3 * 13))}px`,
      '--burst-y': `${Math.round(Math.sin(angle) * (46 + index % 3 * 13))}px`,
      '--burst-turn': `${index * 53}deg`,
      '--burst-delay': `${index % 3 * 25}ms`,
    } as CSSProperties} />;
  })}</span>;
}

// Keep the exact server value readable from the first frame; only its wrapper
// bounces when the value changes, including after a new session resets to zero.
export function ScorePunch({ value, children }: { value: number; children?: ReactNode }) {
  return <span className="qpug-score-punch" key={value}>{children ?? value.toLocaleString('fr-CH')}</span>;
}

export function usePopReveal(enabled?: boolean) {
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const animate = enabled ?? !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!root.current || !animate || !('IntersectionObserver' in window)) return;
    const targets = root.current.querySelectorAll<HTMLElement>('[data-pop-reveal]');
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.removeAttribute('data-pop-wait');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.08 });
    targets.forEach(target => { target.setAttribute('data-pop-wait', ''); observer.observe(target); });
    return () => { observer.disconnect(); targets.forEach(target => target.removeAttribute('data-pop-wait')); };
  }, [enabled]);
  return root;
}
